/** @file Human label of a chord by physical key position (Ctrl+Shift+Z; ⇧⌘Z on mac); never throws. */
import { parseKeys } from './parseKeys';
import type { Chord, ParsedChord, Platform } from './types';

const NAMED: Readonly<Record<string, string>> = {
  Comma: ',', Period: '.', Slash: '/', Backslash: '\\', Semicolon: ';', Quote: "'", BracketLeft: '[', BracketRight: ']',
  Minus: '-', Equal: '=', Backquote: '`', Space: 'Space', Enter: 'Enter', Escape: 'Esc', Backspace: 'Backspace',
  Delete: 'Del', Insert: 'Ins', Tab: 'Tab', ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→',
};

/** `KeyZ` → `Z`, `Digit1` → `1`, `Numpad5` → `Num 5`, punctuation by its US-layout position, else the code. */
function keyLabel(code: string): string {
  const named = NAMED[code];
  if (named !== undefined) return named;
  const letter = /^(?:Key|Digit)(.)$/.exec(code)?.[1];
  if (letter !== undefined) return letter;
  return code.startsWith('Numpad') ? `Num ${code.slice(6)}` : code;
}

function parsed(chord: Chord, platform: Platform): ParsedChord | null {
  try {
    return parseKeys(chord, platform);
  } catch {
    return null;
  }
}

const macLabel = (p: ParsedChord, key: string): string => `${p.ctrl ? '⌃' : ''}${p.alt ? '⌥' : ''}${p.shift ? '⇧' : ''}${p.meta ? '⌘' : ''}${key}`;

function pcLabel(p: ParsedChord, key: string, platform: Platform): string {
  const meta = platform === 'win' ? 'Win' : 'Super';
  return [p.ctrl ? 'Ctrl' : '', p.alt ? 'Alt' : '', p.shift ? 'Shift' : '', p.meta ? meta : '', key].filter((part) => part !== '').join('+');
}

/** The Settings screen's chord text; a malformed chord is returned unchanged. */
export function chordLabel(chord: Chord, platform: Platform): string {
  const p = parsed(chord, platform);
  if (p === null) return chord;
  return platform === 'mac' ? macLabel(p, keyLabel(p.code)) : pcLabel(p, keyLabel(p.code), platform);
}
